import type { AppUser } from "../../../services/userService";
import type { KundeSummary, TodoDto, VorgangDto } from "../../../services/vorgangService";
import { canEditTodo, canEditVorgang, kundeName, teamKontext } from "../../../utils/vorgangScope";
import { resolvePeople } from "./people";
import { deriveVorgangStatus } from "./vorgangStatus";
import { type PeopleGroups, type PersonAvatar } from "./PeopleCell";
import { type ClusterItem } from "../../dashboard/useTodoClusters";
import { teilnehmerAnzeigen, type TeilnehmerAnzeige } from "../v4/austauschAnzeige";

/**
 * Das Zeilenmodell der Vorgangstabelle — herausgelöst aus `VorgaengeMo.tsx`,
 * seit der Vorgänge-Reiter der Kontaktakte dieselbe Tabelle zeigt. Beide bauen
 * ihre Zeilen hier, damit Status, Beteiligte und Schreibrecht in den zwei
 * Ansichten nicht auseinanderlaufen.
 */

/* ────────────────────── Row-Modell ────────────────────── */

export type RowKind = "vorgang" | "todo";

/**
 * Eine flache Zeile der kombinierten Vorgänge-+Todos-Liste. Erfüllt `ClusterItem`
 * (frist/twv/status). Anzeige-Ableitungen (Kunde, Beteiligte, Ersteller)
 * werden im `rows`-Memo aufgelöst, damit die Zell-Renderer rein bleiben.
 */
export interface UnifiedRow extends ClusterItem {
    kind: RowKind;
    id: number;
    titel: string;
    /** Vorgangsart (nur bei kind='vorgang') — Chip vor dem Betreff. */
    vorgangsart?: string | null;
    kundeLabel: string;
    kunde?: KundeSummary | null;
    /**
     * I1/E07: persönlicher Vorgang — ohne Kontakt. Die Kontakt-Spalte zeigt
     * statt der leeren Zelle das Badge „Persönlich"; K-Verantwortlich bleibt
     * leer (es gibt keinen Kontakt, von dem sie geerbt würde).
     */
    persoenlich?: boolean;
    /** Aus einer alten Kundenakte übernommen — Badge „Importiert". */
    importiert?: boolean;
    /** Offen | Erledigt — abgeleitet (Status-Filter). */
    statusValue: string;
    erledigt: number;
    gesamt: number;
    /** ISO-Erstellzeitpunkt — Sortierung „neueste zuerst". */
    createdAt?: string;
    /** Beteiligte, nach Rolle gruppiert (HV / Bearbeiter / zu informieren). */
    beteiligte: PeopleGroups;
    /** Kanonische User-Ids aller Beteiligten (People-Filter). */
    beteiligteUserIds: string[];
    /** Ersteller (Vorgang: createdBy ?? owner; Todo: createdBy). */
    ersteller: PersonAvatar | null;
    /** Markiert eine eingerückte Todo-Kindzeile eines ausgeklappten Vorgangs. */
    isChild?: boolean;
    /**
     * Eltern-Vorgang einer To-do-Zeile (nur kind === "todo"). To-do-Zeilen
     * entstehen hier ausschließlich als Kinder eines geladenen Vorgangs — die Id
     * stammt daher direkt von diesem und NICHT aus dem Todo-DTO (das trägt
     * keine `vorgangId`, die Entity mappt den Parent nur als Relation).
     */
    vorgangId?: number;
    /** Todos eines Vorgangs (nur kind === "vorgang"). */
    children?: UnifiedRow[];
    /** Nur für kind === "todo" (Status-Chip braucht das rohe DTO). */
    todo?: TodoDto;
    /**
     * Darf der angemeldete User DIESE Zeile löschen? Pro Zeile ausgewertet
     * (canEditVorgang/canEditTodo) statt global — jeder darf seine eigenen
     * Vorgänge löschen, fremde nicht. Spiegelt `canUserDeleteVorgang` im Backend,
     * das die finale Absicherung bleibt.
     */
    canDelete: boolean;
    /**
     * Die offenen Austausche am Vorgang und seinen To-dos (T-302, seit
     * 01.11.2026 mit allen Personen) — die Spalte „Austausch". Kürzel und
     * Namen sind hier schon aufgelöst, damit die Zelle rein bleibt. Nur an
     * Vorgangszeilen.
     */
    austausch?: ZeilenAustausch | null;
}

/** Was der Austausch-Chip einer Zeile braucht. */
export interface ZeilenAustausch {
    /** Wie viele Stränge (Vorgang/To-dos) einen offenen Austausch haben. */
    anzahl: number;
    /** Mit wem — über alle offenen zusammengefasst, ohne Doppelte. */
    personen: TeilnehmerAnzeige[];
}

/** Wer die Zeilen sieht — entscheidet über Namen und Schreibrecht. */
export interface ZeilenKontext {
    users: AppUser[];
    currentUserId: string | null;
    isAdmin: boolean;
    isGfgl: boolean;
    /**
     * Die Teams des Anmelders (`teams` aus dem AuthContext). Damit zählt auch
     * „mein Team ist das BO-Team des Kunden" als Kontaktverantwortlichkeit
     * (PO 16.09.2026). Optional — ohne die Liste bleibt das Schreibrecht der
     * Zeile exakt wie vorher.
     */
    teams?: ReadonlyArray<{ id: string }> | null;
}

/* ────────────────────── Beteiligte auflösen ────────────────────── */

/**
 * Rohe Personen-Schlüssel (User-Id, E-Mail oder Kürzel) in Anzeige-Avatare
 * übersetzen — inklusive der kanonischen User-Id, an der das Entra-Profilfoto
 * hängt. Exportiert, weil auch die Kontakttabellen-Übersicht dieselben
 * Beteiligten-Kreise zeigt (PO 08.09.2026: „statt Bearbeiter Beteiligte, mit
 * Bild oder Kürzel").
 */
export function personenAvatare(raw: unknown[] | undefined, users: AppUser[]): PersonAvatar[] {
    return resolvePeople(raw, users);
}

/** Die drei Beteiligten-Rollen eines Vorgangs/To-dos als Anzeige-Gruppen. */
export function beteiligteGruppen(
    users: AppUser[],
    hv?: unknown[],
    be?: unknown[],
    info?: unknown[],
): PeopleGroups {
    return {
        hauptverantwortlich: personenAvatare(hv, users),
        bearbeiter: personenAvatare(be, users),
        zuInformierende: personenAvatare(info, users),
    };
}

/** Kanonische User-Ids aller Beteiligten — ohne die, die nur als Kürzel bekannt sind. */
export function beteiligteUserIds(g: PeopleGroups): string[] {
    const s = new Set<string>();
    for (const p of [...g.hauptverantwortlich, ...g.bearbeiter, ...g.zuInformierende]) {
        if (p.userId) s.add(p.userId);
    }
    return [...s];
}

/** Vorgänge (mit ihren To-dos als Kindzeilen) in Tabellenzeilen übersetzen. */
export function baueVorgangZeilen(
    vorgaenge: VorgangDto[],
    { users, currentUserId, isAdmin, isGfgl, teams }: ZeilenKontext,
): UnifiedRow[] {
        const teamCtx = teamKontext(teams);
        const toAvatars = (raw: unknown[] | undefined): PersonAvatar[] => personenAvatare(raw, users);
        const groupsOf = (hv?: unknown[], be?: unknown[], info?: unknown[]): PeopleGroups =>
            beteiligteGruppen(users, hv, be, info);
        const idsOf = beteiligteUserIds;
        const erstellerOf = (rawKey: string | null | undefined): PersonAvatar | null => {
            if (!rawKey) return null;
            return toAvatars([rawKey])[0] ?? null;
        };

        const buildTodoRow = (t: TodoDto, vorgangId: number, isChild = false): UnifiedRow => {
            const beteiligte = groupsOf(t.hauptverantwortlich, t.bearbeiter, t.zuInformierende);
            return {
                kind: "todo",
                id: t.id,
                vorgangId,
                titel: t.titel,
                kundeLabel: kundeName(t.kunde),
                kunde: t.kunde ?? null,
                statusValue: t.status,
                status: t.status,
                erledigt: 0,
                gesamt: 0,
                frist: t.frist,
                twv: t.twv,
                createdAt: t.createdAt,
                beteiligte,
                beteiligteUserIds: idsOf(beteiligte),
                ersteller: erstellerOf(t.createdByUserId),
                isChild,
                todo: t,
                canDelete: canEditTodo(t, currentUserId, isAdmin, isGfgl, teamCtx),
            };
        };
        const vRows: UnifiedRow[] = vorgaenge.map((v) => {
            const beteiligte = groupsOf(v.hauptverantwortlich, v.bearbeiter as unknown[], v.zuInformierende);
            // Schreibrecht: identische Prüfung für Löschen UND Status-Toggle —
            // beides ist „diesen Vorgang bearbeiten". Backend bleibt die finale Instanz.
            const darfBearbeiten = canEditVorgang(v, currentUserId, isAdmin, isGfgl, teamCtx);
            const austausch = v.offenerAustausch ?? null;
            return {
                kind: "vorgang",
                id: v.id,
                titel: v.titel,
                vorgangsart: v.vorgangsart ?? null,
                kundeLabel: kundeName(v.kunde),
                kunde: v.kunde ?? null,
                persoenlich: v.persoenlich === true,
                importiert: v.importiert === true,
                statusValue: deriveVorgangStatus(v),
                status: deriveVorgangStatus(v),
                erledigt: v.fortschrittErledigt,
                gesamt: v.fortschrittGesamt,
                frist: v.frist,
                twv: v.twv,
                createdAt: v.createdAt,
                beteiligte,
                beteiligteUserIds: idsOf(beteiligte),
                ersteller: erstellerOf(v.createdByUserId ?? v.ownerUserId ?? null),
                canDelete: darfBearbeiten,
                austausch: austausch
                    ? {
                          anzahl: Math.max(1, Number(austausch.anzahl) || 1),
                          personen: teilnehmerAnzeigen(austausch.teilnehmer ?? [], users),
                      }
                    : null,
                children: (v.todos ?? []).map((t) => buildTodoRow(t, v.id, true)),
            };
        });
        // KEINE Client-Sortierung mehr: der Server liefert die Seite bereits in der
        // gewünschten Reihenfolge (Default createdAt DESC, sonst die gewählte
        // Spaltensortierung). Ein Nachsortieren im Client würde beim Infinite-Scroll
        // ohnehin nur die schon geladenen Zeilen umsortieren und die Server-Sortierung
        // über den Gesamtbestand zerstören.
        return vRows;
}

/**
 * Ziel eines Klicks auf eine Zeile der Vorgangsübersicht. Eine To-do-Zeile
 * öffnet den Eltern-Vorgang mit diesem To-do aufgeklappt (`?todo=`, #2339).
 * Fehlt der Eltern-Vorgang wider Erwarten: `null` statt `/vorgaenge/undefined`.
 */
export function zeilenLink(r: Pick<UnifiedRow, "kind" | "id" | "vorgangId">): string | null {
    if (r.kind === "vorgang") return `/vorgaenge/${r.id}`;
    return r.vorgangId != null ? `/vorgaenge/${r.vorgangId}?todo=${r.id}` : null;
}
